import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatetimeColumn } from 'src/feature/data-grid/column/use-datetime-column';
import { useEnergyDeliveredColumn } from 'src/feature/data-grid/column/use-energy-delivered-column';
import { APP_PATH } from 'src/config/app-paths';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTextColumn } from 'src/feature/data-grid/column/use-text-column';
import { useCurrencyColumn } from 'src/feature/data-grid/column/use-currency-column';
import { ChargingSessionModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/back-office/src/models/charging-sessions/charging-session.model';
import { TransactionStatusEnum } from '@delmonicos/shared-sdk/libs/emsp/src/libs/shared/src/transaction-service/enum/transaction-status.enum';
import { useGetChargingSessionStatusText } from '../../../shared/charging-status-card/use-get-charging-session-status-text';

export function useChargingSessionColumn(): GridColDef[] {
  const { t } = useTranslation();

  const chargingSessionTranslations: Record<
    keyof ChargingSessionModel,
    string
  > = t('model.charging-session', {
    returnObjects: true,
  });

  const textColumn = useTextColumn();
  const datetimeColumn = useDatetimeColumn();
  // const pseudoColumn = usePseudoColumn();
  const energyDeliveredColumn = useEnergyDeliveredColumn();
  const currencyColumn = useCurrencyColumn();
  const getSessionStatus = useGetChargingSessionStatusText();

  return useMemo(
    () => [
      {
        ...textColumn,
        field: 'locationName',
        width: 160,
        headerName: chargingSessionTranslations.locationName,
      },
      {
        ...textColumn,
        field: 'locationCity',
        width: 160,
        headerName: chargingSessionTranslations.locationCity,
      },
      {
        ...datetimeColumn,
        field: 'startedAt',
        headerName: chargingSessionTranslations.startedAt,
      },
      {
        ...datetimeColumn,
        field: 'stoppedAt',
        headerName: chargingSessionTranslations.stoppedAt,
      },
      {
        ...energyDeliveredColumn,
        field: 'energyDelivered',
        headerName: chargingSessionTranslations.energyDelivered,
      },
      {
        ...currencyColumn,
        field: 'totalTTC',
        headerName: chargingSessionTranslations.totalTTC,
      },
      {
        ...textColumn,
        field: 'status',
        width: 160,
        sortable: false,
        headerName: chargingSessionTranslations.status,
        valueGetter: (
          params: GridValueGetterParams<
            ChargingSessionModel,
            ChargingSessionModel['status']
          >,
        ): TransactionStatusEnum | null => {
          return (
            [...(params.value ?? [])]
              .sort(
                (a, b) =>
                  new Date(a.updatedAt).getTime() -
                  new Date(b.updatedAt).getTime(),
              )
              .pop()?.type ?? null
          );
        },
        valueFormatter: (
          params: GridValueFormatterParams<TransactionStatusEnum | null>,
        ) => (params.value !== null ? getSessionStatus(params.value) : null),
      },
      {
        field: 'actions',
        type: 'actions',
        width: 80,

        getActions: (params: GridRowParams<ChargingSessionModel>) => [
          <GridActionsCellItem
            key="link-to-detail"
            component={RouterLink}
            target="_blank"
            label={t('charging-session.data-grid.cta.view-detail')}
            icon={<OpenInNewIcon />}
            to={generatePath(APP_PATH.CHARGING_SESSION_DETAIL, {
              chargingSessionId: params.row.id,
            })}
          />,
        ],
      },
    ],
    [
      textColumn,
      chargingSessionTranslations.locationName,
      chargingSessionTranslations.locationCity,
      chargingSessionTranslations.startedAt,
      chargingSessionTranslations.stoppedAt,
      chargingSessionTranslations.energyDelivered,
      chargingSessionTranslations.totalTTC,
      chargingSessionTranslations.status,
      datetimeColumn,
      energyDeliveredColumn,
      currencyColumn,
      getSessionStatus,
      t,
    ],
  );
}
